import * as React from "react";
import {
  BackendFailureAlert,
  fetchWithToken,
  hasPermission,
  isFeatureEnabled,
} from "@platform/core";
import { FilterAndSortGridRecords } from "./FilterAndSortGridRecords";
import {
  Alert,
  Box,
  DataGrid7 as DataGrid,
  focusPagination,
  GridColDef,
  GridColumnVisibilityModel,
  GridFilterItem,
  GridFilterModel,
  GridOverlay,
  GridSortModel,
  LinearProgress,
  Snackbar,
} from "@platform/style";
import { WorkItemPage } from "../models/WorkItemPage";
import { WORK_ITEMS_BFF_API_BASE } from "../platform-workitems";
import { reportError } from "../utils/errors";
import { WorkItemGridColumns } from "./WorkItemGridColumns";
import { WorkItemControlBar } from "./WorkItemControlBar";
import { Dispatch, SetStateAction } from "react";
import { ActionEnum } from "../models/ReasonsAndResolutions";
import { MassActionPopUpDialogBox } from "./MassActionPopUpDialogBox";
import { CloseMassActionRequest } from "../models/CloseMassActionRequest";
import { MassFilterGridRecords } from "./MassFilterGridRecords";
import { AssignMassActionRequest } from "../models/AssignMassActionRequest";
import { Metrics } from "../models/Metrics";
import { fetchDataGridInitialData } from "../utils/fetchDataGridInitialData";
import { WorkItemPageTypes } from "../models/WorkItemPageTypes";
import { getMetrics } from "./workItems/apiClient";
import { updateMetrics } from "../utils/metricsCardConfigData";

interface WorkItemDataGridProps {
  typeOfWorkItemPage: string;
  workItemPage: WorkItemPage;
  setUri: Dispatch<SetStateAction<string | null>>;
  setMetrics?: Dispatch<SetStateAction<Metrics>>;
  setIsLoaded?: Dispatch<SetStateAction<boolean>>;
  setShowInactiveDefaultMessage?: Dispatch<SetStateAction<boolean>>;
  isLoading: boolean;
  isErrored: boolean;
  refetch: any;
  correlationId: string;
  errorStack: string;
}

function CustomNoRowsOverlay() {
  return (
    <GridOverlay>
      <div>No Work Items to Display</div>
    </GridOverlay>
  );
}

const initialFilterModel: GridFilterModel = {
  items: [
    {
      field: "Title",
      operator: "contains",
      value: "",
    },
  ],
};

enum Fields {
  Associations = "Number",
  BusinessFunction = "Business Function",
  Title = "Title",
  Status = "Status",
  BusinessProcess = "Business Process",
  CreatedDatetime = "Created Date",
  DueDate = "Due Date",
  FollowUpDate = "Follow Up Date",
  AssignedTo = "Assigned To",
}

export const WorkItemDataGrid = ({
  typeOfWorkItemPage,
  workItemPage,
  setUri,
  setMetrics,
  setIsLoaded,
  setShowInactiveDefaultMessage,
  isLoading,
  isErrored,
  refetch,
  correlationId,
  errorStack,
}: WorkItemDataGridProps) => {
  const columnVisibility: GridColumnVisibilityModel = {
    SelectRows: false,
    AssignedTo: typeOfWorkItemPage == WorkItemPageTypes.ActivePage,
    Priority: typeOfWorkItemPage !== WorkItemPageTypes.InactivePage,
    CreatedDatetime: typeOfWorkItemPage !== WorkItemPageTypes.InactivePage,
    DueDate: typeOfWorkItemPage !== WorkItemPageTypes.InactivePage,
    FollowUpDate: typeOfWorkItemPage !== WorkItemPageTypes.InactivePage,
    ClosedDatetime: typeOfWorkItemPage == WorkItemPageTypes.InactivePage,
    ClosedBy: typeOfWorkItemPage == WorkItemPageTypes.InactivePage,
    ResolutionReasonCode: typeOfWorkItemPage == WorkItemPageTypes.InactivePage,
    BusinessArea: false,
    CreatedBy: false,
    Description: false,
  };

  const [pageSize, setPageSize] = React.useState<number>(25);
  const [pageNumber, setPageNumber] = React.useState<number>(1);
  const [isFilterOn, setIsFilterOn] = React.useState<boolean>(false);
  const [massAction, setMassAction] = React.useState<string | undefined>(undefined);
  const [selectedRows, setSelectedRows] = React.useState<string[]>([]);
  const [headerChecked, setHeaderChecked] = React.useState(false);
  const [filterChangedUri, setFilterChangedUri] = React.useState<string | null>(null);

  const [queryServer, setQueryServer] = React.useState<boolean>(true);
  const [filterModel, setFilterModel] = React.useState<GridFilterModel>(initialFilterModel);
  const [filterButtonEl, setFilterButtonEl] = React.useState<HTMLButtonElement | null>(null);
  const [workItemInteractionError, setWorkItemInteractionError] = React.useState<string | null>(
    null
  );
  const [openSnackBar, setSnackBarOpen] = React.useState(false);
  const [snackBarMessage, setSnackBarMessage] = React.useState("");
  const [isDuplicateColFilterError, setIsDuplicateColFilterError] = React.useState(false);
  const [popUpOpen, setPopUpOpen] = React.useState<boolean>(false);

  const [functions, setFunctions] = React.useState<string[]>([]);
  const [processes, setProcesses] = React.useState<string[]>([]);
  const [resources, setResources] = React.useState<{ value: string; label: string }[]>([]);
  const [completeReasonCodes, setCompleteReasonCodes] = React.useState<string[]>([]);
  const [cancelReasonCodes, setCancelReasonCodes] = React.useState<string[]>([]);
  const [sortModel, setSortModel] = React.useState<GridSortModel>([]);
  const [columnVisibilityModel, setColumnVisibilityModel] =
    React.useState<GridColumnVisibilityModel>(columnVisibility);
  const [metricsAccess, setMetricsAccess] = React.useState<boolean>(false);

  React.useEffect(() => {
    (async () => {
      if (
        typeOfWorkItemPage === WorkItemPageTypes.ActivePage &&
        (await hasPermission("WorkManagementAdmin.massAction")) &&
        (await isFeatureEnabled("WorkItems.MassActions"))
      ) {
        const newColumnVisibilityModel = columnVisibility;
        newColumnVisibilityModel.SelectRows = true;
        setColumnVisibilityModel(newColumnVisibilityModel);
      }
    })();
  }, [typeOfWorkItemPage]);

  React.useEffect(() => {
    (async () => {
      setMetricsAccess(
        (await hasPermission("WorkManagementAdmin.viewMetrics")) &&
          (await isFeatureEnabled("WorkItems.Metrics"))
      );
    })();
  }, []);

  React.useEffect(() => {
    fetchDataGridInitialData(
      typeOfWorkItemPage,
      setFunctions,
      setProcesses,
      setCompleteReasonCodes,
      setCancelReasonCodes,
      setResources,
      setWorkItemInteractionError
    );
    setIsLoaded?.(false);
    if (typeOfWorkItemPage === WorkItemPageTypes.InactivePage)
      setShowInactiveDefaultMessage?.(true);
  }, []);

  React.useEffect(() => {
    setSelectedRows([]);
    setHeaderChecked(false);
    setIsLoaded?.(false);
  }, [filterChangedUri]);

  React.useEffect(() => {
    if (queryServer) {
      const serverQueryUri = FilterAndSortGridRecords(
        typeOfWorkItemPage,
        pageSize,
        pageNumber,
        isFilterOn ? filterModel.items : undefined,
        sortModel ?? undefined
      );
      const secondIndexOfChar = serverQueryUri.indexOf("&", serverQueryUri.indexOf("&") + 1);
      setFilterChangedUri(serverQueryUri.substring(secondIndexOfChar + 1));
      setUri(serverQueryUri);
    }
    if (setShowInactiveDefaultMessage && typeOfWorkItemPage === WorkItemPageTypes.InactivePage) {
      isFilterOn ? setShowInactiveDefaultMessage(false) : setShowInactiveDefaultMessage(true);
    }
  }, [
    typeOfWorkItemPage,
    pageSize,
    pageNumber,
    isFilterOn,
    filterModel.items,
    sortModel,
    queryServer,
  ]);

  const closeWorkItemInteractionErrorAlert = () => {
    setWorkItemInteractionError(null);
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.code == "Space") {
      (e.target as HTMLElement).click();
    }
  };

  React.useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  function handlePageSizeChange(newPageSize: number): void {
    setPageSize(newPageSize);
  }

  function handlePaginationChange(value: number): void {
    setPageNumber(value);
  }

  function removeSortByQueryFromUri(uri: string): string {
    const regex = /&sortBy=[^&]*/g;
    return uri.replace(regex, "");
  }

  React.useEffect(() => {
    if (filterChangedUri && metricsAccess) {
      const updatedFilterChangedUri = removeSortByQueryFromUri(filterChangedUri);

      const isCompletedOrCancelled =
        RegExp(/(^|&)workItemStatuses=COMPLETED(&|$)/).exec(updatedFilterChangedUri) ||
        RegExp(/(^|&)workItemStatuses=CANCELLED(&|$)/).exec(updatedFilterChangedUri);

      if (!isCompletedOrCancelled && typeOfWorkItemPage != WorkItemPageTypes.MyWorkPage) {
        const workItemPageType: string =
          typeOfWorkItemPage === WorkItemPageTypes.InactivePage ? "Inactive" : "Active";

        getMetrics(workItemPageType, updatedFilterChangedUri)
          .then((metrics) => {
            if (metrics && setMetrics) {
              metrics.totalWorkCount = workItemPage.TotalRecords;
              setCancelledCount(metrics, updatedFilterChangedUri);
              setUnAssignedCount(metrics, updatedFilterChangedUri);
              updateMetrics(metrics, setMetrics);
              setIsLoaded?.(true);
            }
          })
          .catch((error) => {
            if (setMetrics) {
              setMetrics({
                assignedCount: 0,
                targetPastDueDateCount: 0,
                dueTodayCount: 0,
                completedCount: 0,
                unAssignedCount: 0,
                cancelledCount: 0,
                totalWorkCount: workItemPage.TotalRecords,
              });
            }
            console.error("Error fetching metrics:", error);
          });
      } else {
        const isCompleted = updatedFilterChangedUri.includes("workItemStatuses=COMPLETED");
        const isCancelled = updatedFilterChangedUri.includes("workItemStatuses=CANCELLED");

        if (setMetrics) {
          setMetrics({
            completedCount: isCompleted ? workItemPage.TotalRecords : 0,
            cancelledCount: isCancelled ? workItemPage.TotalRecords : 0,
            totalWorkCount: workItemPage.TotalRecords,
          });
        }
        setIsLoaded?.(true);
      }
    }
  }, [workItemPage]);

  function setCancelledCount(metrics: Metrics, filterChangedUri: string) {
    if (filterChangedUri.trim().replace(/,\s+/g, ",")) {
      metrics.cancelledCount =
        workItemPage.TotalRecords - (metrics.completedCount ?? 0) > 0
          ? workItemPage.TotalRecords - (metrics.completedCount ?? 0)
          : 0;
    }
  }

  function setUnAssignedCount(metrics: Metrics, filterChangedUri: string) {
    if (filterChangedUri.includes("assignees")) {
      metrics.unAssignedCount = 0;
    } else if (filterChangedUri.trim().replace(/,\s+/g, ",")) {
      metrics.unAssignedCount = workItemPage.TotalRecords - (metrics.assignedCount ?? 0);
    } else {
      metrics.unAssignedCount = 0;
    }
  }

  function handleMassAction(changeAction: string): void {
    setMassAction(changeAction);
    setPopUpOpen(true);
  }

  function handleRowSelection(rowId: string, checked: boolean, isHeader: boolean): void {
    if (isHeader) {
      setHeaderChecked(checked);
      checked
        ? setSelectedRows([workItemPage.WorkItems[0]?.WorkItemIdentifier])
        : setSelectedRows([]);
    } else {
      checked
        ? setSelectedRows([...selectedRows, rowId])
        : setSelectedRows(selectedRows.filter((id) => id !== rowId));
    }
  }

  function isChecked(rowId: string): boolean {
    return !!selectedRows.find((id) => id == rowId);
  }

  const getTitle = () => {
    if (massAction == ActionEnum.CANCEL) return "Cancel Work Items";
    if (massAction == ActionEnum.COMPLETE) return `Complete Work Items`;
    if (massAction == ActionEnum.ASSIGN) return `Assign Work Items`;
    return "";
  };

  const handlePopUpModalClose = async () => {
    setMassAction(undefined);
    setPopUpOpen(false);
  };

  function createMassActionRequest(
    massAction: string | undefined,
    resolutionCode: string | undefined,
    comment: string | undefined,
    assignedTo: string | undefined
  ) {
    if (headerChecked) {
      return MassFilterGridRecords(
        massAction ?? "",
        resolutionCode ?? "",
        comment,
        assignedTo ?? "",
        isFilterOn ? filterModel.items : undefined
      );
    }
    if (massAction === ActionEnum.ASSIGN) {
      return {
        WorkItemIdentifiers: selectedRows,
        AssignToIdentifier: assignedTo,
        Comment: comment,
      } as AssignMassActionRequest;
    }
    if (massAction === ActionEnum.CANCEL || massAction === ActionEnum.COMPLETE) {
      return {
        WorkItemIdentifiers: selectedRows,
        ResolutionReasonCode: resolutionCode,
        Comment: comment,
      } as CloseMassActionRequest;
    }
  }

  function handleMassActionSubmit(
    massAction: string | undefined,
    resolutionCode: string | undefined,
    comment: string | undefined,
    assignedTo: string | undefined
  ) {
    setPopUpOpen(false);
    const massActionRequest = createMassActionRequest(
      massAction,
      resolutionCode,
      comment,
      assignedTo
    );

    const apiEndpoint = `${WORK_ITEMS_BFF_API_BASE}/${massAction?.toLowerCase()}?isBulkSelect=${headerChecked}`;

    (async () => {
      await fetchWithToken(apiEndpoint, {
        method: "POST",
        body: JSON.stringify(massActionRequest),
      })
        .then(async (response) => {
          if (!response.ok) {
            throw new Error(JSON.stringify(response));
          }
          refetch();
          setHeaderChecked(false);
          setSelectedRows([]);
          setMassAction(undefined);
          setSnackBarOpen(true);
          setSnackBarMessage(() => {
            let message = "";
            if (massAction === ActionEnum.ASSIGN) {
              message = "Work Items have been assigned.";
            } else if (massAction === ActionEnum.CANCEL) {
              message = "Work Items have been cancelled.";
            } else {
              message = "Work Items have been completed.";
            }
            return message;
          });
        })
        .catch((error) => {
          setWorkItemInteractionError(
            `Unable to perform mass ${massAction?.toLowerCase()} action. Please try again.`
          );
          reportError(error.message);
        });
    })();
  }

  const handleSortModelChange = (newSortModel: GridSortModel) => {
    setSortModel(newSortModel);
  };

  function WorkItemToolBar() {
    return (
      <WorkItemControlBar
        setFilterButtonEl={setFilterButtonEl}
        isFilterOn={isFilterOn}
        setQueryServer={setQueryServer}
        queryServer={queryServer}
        initialFilterState={initialFilterModel}
        filterModel={filterModel}
        massActionClick={handleMassAction}
        showMassActionIcons={
          headerChecked ? workItemPage.TotalRecords > 0 : selectedRows.length > 0
        }
      />
    );
  }
  return (
    <>
      {isLoading ? <LinearProgress /> : null}
      {workItemInteractionError && (
        <Alert
          onClose={!isDuplicateColFilterError ? closeWorkItemInteractionErrorAlert : undefined}
          severity="error"
          sx={{
            width: "calc(100% - 34px)",
            borderColor: "error.main",
            borderWidth: "1px",
            borderStyle: "solid",
            mt: typeOfWorkItemPage == WorkItemPageTypes.MyWorkPage ? 1 : 6,
          }}
        >
          {workItemInteractionError}
        </Alert>
      )}

      {isErrored ? (
        <Box sx={{ mt: 4.5, height: "4px" }}>
          <BackendFailureAlert
            retryFunction={refetch}
            correlationId={correlationId}
            exceptionInformation={errorStack}
            component={"WorkItemGrid"}
          />
        </Box>
      ) : (
        <DataGrid
          sx={{
            "& .MuiDataGrid-filterIcon": {
              display: "none",
            },
            "& .MuiDataGrid-virtualScrollerContent": {
              borderRight: `none !important`,
              borderLeft: `none !important`,
            },
            "& .MuiDataGrid-virtualScroller": {
              borderRight: `1px solid lightgrey !important`,
              borderLeft: `1px solid lightgrey !important`,
            },
            paddingTop: "0px !important",
            display: "grid",
            gridTemplateRows: "min-content auto min-content",
            "& .MuiDataGrid-columnHeaderTitleContainer": {
              paddingBottom: "10px",
              marginBottom: "0px",
            },
          }}
          disableColumnMenu
          columnBufferPx={12}
          rows={workItemPage.WorkItems}
          rowCount={workItemPage.TotalRecords}
          getRowId={(row) => row.WorkItemIdentifier}
          rowHeight={60}
          pagination
          paginationMode="server"
          filterMode="server"
          columnVisibilityModel={columnVisibilityModel}
          paginationModel={{ page: pageNumber - 1, pageSize }}
          pageSizeOptions={[25, 50, 100]}
          onPaginationModelChange={(model) => {
            handlePaginationChange(model.page + 1);
            handlePageSizeChange(model.pageSize);
          }}
          slots={{
            toolbar: WorkItemToolBar,
            noRowsOverlay: CustomNoRowsOverlay,
          }}
          onSortModelChange={handleSortModelChange}
          onColumnVisibilityModelChange={(newModel) => setColumnVisibilityModel(newModel)}
          columns={
            WorkItemGridColumns(
              typeOfWorkItemPage,
              setWorkItemInteractionError,
              refetch,
              functions,
              processes,
              resources,
              [...completeReasonCodes, ...cancelReasonCodes],
              handleRowSelection,
              isChecked,
              headerChecked,
              headerChecked ? workItemPage.TotalRecords > 0 : selectedRows.length > 0,
              setSnackBarOpen,
              setSnackBarMessage
            ) as GridColDef[]
          }
          localeText={{
            toolbarFilters: "",
            footerRowSelected: () =>
              `Total Selected: ${
                headerChecked ? Math.max(workItemPage.TotalRecords, 0) : selectedRows.length
              }`,
          }}
          slotProps={{
            pagination: {
              sx: { ...focusPagination },
            },
            filterPanel: {
              logicOperators: [],
              disableRemoveAllButton: true,
            },
          }}
          rowSelectionModel={selectedRows}
          onFilterModelChange={(newFilterModel, details) => {
            if (details.reason == "upsertFilterItems") {
              newFilterModel.items.pop();

              let filterModelFields = createFilterModelFieldsBasedOnPageType(typeOfWorkItemPage);
              let cols = filterModelFields.items.filter(
                (filterModelField) =>
                  !newFilterModel.items.some((item) => item.field === filterModelField.field)
              );
              cols.length > 0 && newFilterModel.items.push(cols[0]);
            }

            if (newFilterModel.items.length > 0) {
              let errorMessage: string = "";
              let duplicateFieldCount: number = 0;
              Object.entries(Fields).forEach(([key, value]) => {
                const count = newFilterModel.items.reduce(
                  (occurrence, item) => (item.field === key ? ++occurrence : occurrence),
                  0
                );
                if (count > 1) {
                  newFilterModel.items.forEach((item) => {
                    if (item.field == key) {
                      item.value = "";
                    }
                  });
                  duplicateFieldCount += 1;
                  errorMessage += `${duplicateFieldCount > 1 ? ", " : ""}${value}`;
                }
              });

              if (duplicateFieldCount > 0) {
                setWorkItemInteractionError(`Duplicate column(s) selected: ${errorMessage}.`);
                setIsDuplicateColFilterError(true);
              } else {
                setWorkItemInteractionError(null);
                setIsDuplicateColFilterError(false);
              }

              setIsFilterOn(
                !!newFilterModel.items.find(
                  (item) =>
                    item.value?.trim() !== "" &&
                    item.value !== undefined &&
                    item.value !== "Invalid Date"
                )
              );
            } else {
              setIsFilterOn(false);
            }
            setFilterModel(newFilterModel);
            setQueryServer(false);
          }}
          onPreferencePanelClose={() => {
            const validFilters = filterModel.items.filter(
              (item) =>
                item.value?.trim() !== "" &&
                item.value !== undefined &&
                item.value !== "Invalid Date"
            );

            if (validFilters.length !== filterModel.items.length) {
              setFilterModel({ ...filterModel, items: validFilters });
            }
          }}
        />
      )}
      {
        <Snackbar
          message={snackBarMessage}
          open={openSnackBar}
          onClose={() => {
            setSnackBarOpen(false);
          }}
        />
      }

      <MassActionPopUpDialogBox
        typeOfMassAction={massAction ?? ""}
        title={getTitle()}
        selectedRowCount={headerChecked ? workItemPage.TotalRecords : selectedRows.length}
        open={popUpOpen}
        onClose={handlePopUpModalClose}
        onSubmit={(resolutionCode, comment, assignToWorker) =>
          handleMassActionSubmit(massAction, resolutionCode, comment, assignToWorker)
        }
      />
    </>
  );
};

function createFilterModelFieldsBasedOnPageType(typeOfWorkItemPage: string): GridFilterModel {
  let filterModelFields: GridFilterModel;
  if (typeOfWorkItemPage == WorkItemPageTypes.InactivePage) {
    filterModelFields = { items: [...baseGridFilterItems, ...inactiveGridFilterItems] };
  } else {
    filterModelFields = {
      items: [...baseGridFilterItems, ...activeAndMyWorkItemGridFilterItems],
    };
  }
  return filterModelFields;
}

const baseGridFilterItems: GridFilterItem[] = [
  {
    field: "Associations",
    operator: "contains",
    value: "",
  },
  {
    field: "BusinessFunction",
    operator: "is",
    value: "",
  },
  {
    field: "Title",
    operator: "contains",
    value: "",
  },
  {
    field: "Status",
    operator: "is",
    value: "",
  },
  {
    field: "BusinessProcess",
    operator: "is",
    value: "",
  },
];

const activeAndMyWorkItemGridFilterItems: GridFilterItem[] = [
  {
    field: "CreatedDatetime",
    operator: "is",
    value: "",
  },
  {
    field: "DueDate",
    operator: "is",
    value: "",
  },
  {
    field: "FollowUpDate",
    operator: "is",
    value: "",
  },
  {
    field: "AssignedTo",
    operator: "is",
    value: "",
  },
];

const inactiveGridFilterItems: GridFilterItem[] = [
  {
    field: "ClosedDatetime",
    operator: "is",
    value: "",
  },
  {
    field: "ClosedBy",
    operator: "is",
    value: "",
  },
  {
    field: "ResolutionReasonCode",
    operator: "is",
    value: "",
  },
];
